import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { remoteConfig } from './auth/supabase'
import { data } from './data'
import type { CaptureTokenInput, CreatedCaptureToken } from './data/types'
import {
  nativeRecorderAvailable,
  nativeUploadTokenState,
  setNativeUploadToken,
  type NativeResult,
  type NativeUploadState,
  type NativeUploadTokenState,
} from './native-recorder'

// ============================================================================
// THE PHONE UPLOADS ON ITS OWN — the web half of the Android app's background uploader.
//
// THE OWNER'S WORDS, 2026-09-24, after his first real class went up correctly: "I want the
// uploading to happen automatically, not have me open the app." The phone can only do that with a
// credential of its own — a signed-in browser session expires, and at 11:10 on a Tuesday the web
// app is not running to refresh it. So, once, while he is signed in, this mints a DEVICE CAPTURE
// TOKEN (`POST /api/capture-tokens`, owner session only) and hands it straight to the plugin,
// which stores it encrypted and never gives it back. The same kind of credential the PC worker
// holds; revocable on the Connections screen like any other.
//
// THE PLAINTEXT NEVER STAYS HERE. It exists in one local variable between the mint's response and
// the hand-over, is never rendered, logged, or put in query cache, and the Worker cannot show it
// again. If the hand-over fails, the screen says so and names the remedy (revoke, try again).
// ============================================================================

/** How the token appears in the Connections list six months from now. */
export const PHONE_UPLOAD_TOKEN_LABEL = 'S24 LifeOS app'

/** What the Recordings screen knows about the phone's own uploader. */
export type PhoneUploader =
  /** Not asked yet, or not inside the Android app at all. Nothing is shown. */
  | { kind: 'unknown' }
  /** This build of the app has no uploader (the command is missing). Nothing is offered. */
  | { kind: 'unsupported'; reason: string }
  | { kind: 'off' }
  | { kind: 'on'; state: NativeUploadTokenState }

/** The outcome of one "Let the phone upload on its own" tap. */
export type TurnOnOutcome =
  | { ok: true; state: NativeUploadTokenState }
  | { ok: false; problem: string }

/**
 * MINT, THEN HAND OVER — the whole sequence, as a pure function of its three doors so every
 * outcome is testable without a phone. Each failure is a sentence the screen shows verbatim.
 */
export async function mintAndHandOver(doors: {
  apiBase: string | null | undefined
  createToken: (input: CaptureTokenInput) => Promise<CreatedCaptureToken>
  handOver: (token: string, apiBase: string, label: string) => Promise<NativeResult<NativeUploadTokenState>>
  label?: string
}): Promise<TurnOnOutcome> {
  const label = doors.label ?? PHONE_UPLOAD_TOKEN_LABEL
  const apiBase = doors.apiBase?.trim()
  if (!apiBase) {
    return { ok: false, problem: 'This build of LifeOS has no server configured, so there is nothing to upload to.' }
  }

  let created: CreatedCaptureToken
  try {
    created = await doors.createToken({ label, device: 'phone' })
  } catch (error) {
    const why = error instanceof Error && error.message ? error.message : 'the server did not answer'
    return { ok: false, problem: `No token was made: ${why}. Nothing changed on this phone.` }
  }

  const handed = await doors.handOver(created.token, apiBase, label)
  if (!handed.available) {
    return {
      ok: false,
      problem:
        `A token was made, but the phone would not store it: ${handed.reason} Revoke “${label}” on ` +
        'the Connections screen, then try again.',
    }
  }
  if (!handed.value.present) {
    return {
      ok: false,
      problem:
        `A token was made, but the phone says it has none. Revoke “${label}” on the Connections ` +
        'screen, then try again.',
    }
  }
  return { ok: true, state: handed.value }
}

/**
 * The phone's uploader, as the screen sees it: asked once on mount (and again when the screen
 * comes back into view), plus the one action that turns it on.
 *
 * The mint goes through TanStack Query as a mutation — the data contract's hook for
 * `createCaptureToken` — and invalidates the Connections screen's token list, which now has one more.
 */
export function usePhoneUploader(): {
  uploader: PhoneUploader
  busy: boolean
  problem: string | null
  turnOn: () => void
} {
  const [uploader, setUploader] = useState<PhoneUploader>({ kind: 'unknown' })
  const [problem, setProblem] = useState<string | null>(null)
  const live = useRef(true)
  const client = useQueryClient()

  const ask = useCallback(async () => {
    if (!nativeRecorderAvailable()) return
    const answer = await nativeUploadTokenState()
    if (!live.current) return
    if (!answer.available) setUploader({ kind: 'unsupported', reason: answer.reason })
    else setUploader(answer.value.present ? { kind: 'on', state: answer.value } : { kind: 'off' })
  }, [])

  useEffect(() => {
    live.current = true
    void ask()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void ask()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live.current = false
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [ask])

  const mint = useMutation({
    mutationFn: () =>
      mintAndHandOver({
        apiBase: remoteConfig()?.apiBaseUrl,
        createToken: (input) => data.createCaptureToken(input),
        handOver: setNativeUploadToken,
      }),
    onSuccess: (outcome) => {
      // A token was minted either way except the first refusal — the list has changed.
      void client.invalidateQueries({ queryKey: ['captureTokens'] })
      if (!live.current) return
      if (outcome.ok) {
        setProblem(null)
        setUploader({ kind: 'on', state: outcome.state })
      } else {
        setProblem(outcome.problem)
      }
    },
  })

  const turnOn = useCallback(() => {
    setProblem(null)
    mint.mutate()
  }, [mint])

  return { uploader, busy: mint.isPending, problem, turnOn }
}

// ── the words ───────────────────────────────────────────────────────────────

/**
 * One native upload, in the phone's four words — plus when, and the Worker's own sentence where
 * there is one. `clock` renders an ISO instant; it is passed in so the words are testable.
 */
export function nativeUploadWords(
  upload: NativeUploadState,
  clock: (iso: string) => string,
): { line: string; problem: string | null } {
  const at = upload.at ? clock(upload.at) : null
  switch (upload.state) {
    case 'queued':
      return upload.attempts > 0
        ? { line: `Waiting to try again${at ? ` · since ${at}` : ''}`, problem: upload.lastError ?? null }
        : { line: 'Waiting for a connection — it goes up on its own', problem: null }
    case 'uploading':
      return { line: `Uploading from the phone${at ? ` · started ${at}` : ''}`, problem: null }
    case 'received':
      return { line: `Uploaded — the server confirmed it${at ? ` at ${at}` : ''}`, problem: null }
    case 'failed':
      return {
        line: 'Not uploaded — it is still on this phone',
        problem: upload.lastError ?? 'The phone gave no reason.',
      }
  }
}
